<template>
  <b-modal
    v-model="show"
    :title="`Incidencias — ${(pedido && pedido.ref) || ''}`"
    size="lg"
    centered
    scrollable
    hide-footer
  >
    <div v-if="cargando" class="text-center text-muted my-2">
      <b-spinner small class="align-middle mr-50" /> Cargando…
    </div>

    <template v-else>
      <!-- Incidencias existentes -->
      <div class="inc-listado__head">
        <span class="inc-listado__titulo">
          {{ incidencias.length }} incidencia{{ incidencias.length === 1 ? '' : 's' }}
        </span>
        <b-button v-if="!mostrarForm" size="sm" variant="primary" @click="mostrarForm = true">
          <i class="fa-solid fa-plus mr-25"></i> Nueva incidencia
        </b-button>
      </div>

      <div v-if="!incidencias.length && !mostrarForm" class="text-muted mb-1">Sin incidencias registradas.</div>

      <div v-for="inc in incidencias" :key="inc.id" class="inc-card" :class="`inc-card--${claseEstado(inc.estado)}`">
        <div class="inc-card__head">
          <span class="inc-num">#{{ inc.id }}</span>
          <b-form-select
            :value="inc.estado"
            :options="estados"
            size="sm"
            class="inc-estado"
            :class="`inc-estado--${claseEstado(inc.estado)}`"
            @change="cambiarEstado(inc, $event)"
          />
          <small class="text-muted ml-auto">
            {{ formatFecha(inc.createdAt) }}<template v-if="inc.usuarioNombre"> · {{ inc.usuarioNombre }}</template>
          </small>
          <b-button size="sm" variant="flat-danger" class="btn-icon" title="Eliminar incidencia" @click="eliminar(inc)">
            <i class="fa-solid fa-trash-can"></i>
          </b-button>
        </div>
        <div class="inc-card__desc">{{ inc.descripcion }}</div>
        <div v-if="inc.notas" class="inc-card__notas"><i class="fa-solid fa-note-sticky mr-25"></i>{{ inc.notas }}</div>
        <div v-if="inc.fotos.length" class="inc-fotos">
          <img
            v-for="fid in inc.fotos"
            :key="fid"
            :src="fotoUrls[fid]"
            class="inc-foto"
            title="Click para ampliar"
            @click="ampliarFoto(fid)"
          />
        </div>

        <!-- Comentarios -->
        <div class="inc-coments">
          <div v-for="c in inc.comentarios" :key="'c' + c.id" class="inc-coment">
            <span class="inc-coment__meta">
              {{ c.usuarioNombre || '—' }} · {{ formatFecha(c.createdAt) }}
              <template v-if="c.mio && editandoId !== c.id">
                <i class="fa-solid fa-pen inc-coment__accion" title="Editar" @click="empezarEdicion(c)"></i>
                <i class="fa-solid fa-trash-can inc-coment__accion inc-coment__accion--rojo" title="Borrar" @click="borrarComentario(c)"></i>
              </template>
            </span>
            <div v-if="editandoId === c.id" class="inc-coment__nuevo">
              <b-form-input
                v-model="editandoTexto"
                size="sm"
                @keyup.enter="guardarEdicion(c)"
                @keyup.esc="editandoId = null"
              />
              <b-button size="sm" variant="outline-primary" class="btn-icon" title="Guardar" :disabled="!editandoTexto.trim()" @click="guardarEdicion(c)">
                <i class="fa-solid fa-check"></i>
              </b-button>
              <b-button size="sm" variant="outline-secondary" class="btn-icon" title="Cancelar" @click="editandoId = null">
                <i class="fa-solid fa-xmark"></i>
              </b-button>
            </div>
            <div v-else class="inc-coment__texto">{{ c.texto }}</div>
          </div>
          <div class="inc-coment__nuevo">
            <b-form-input
              :value="nuevoComentario[inc.id]"
              size="sm"
              placeholder="Escribe un comentario…"
              @input="$set(nuevoComentario, inc.id, $event)"
              @keyup.enter="comentar(inc)"
            />
            <b-button
              size="sm"
              variant="outline-primary"
              class="btn-icon"
              title="Enviar comentario"
              :disabled="!(nuevoComentario[inc.id] || '').trim()"
              @click="comentar(inc)"
            >
              <i class="fa-solid fa-paper-plane"></i>
            </b-button>
          </div>
        </div>
      </div>

      <!-- Nueva incidencia (plegada tras un botón) -->
      <div v-if="mostrarForm" class="inc-nueva">
        <div class="inc-nueva__title">
          <span><i class="fa-solid fa-triangle-exclamation mr-50"></i>Nueva incidencia</span>
          <button class="inc-nueva__cerrar" @click="mostrarForm = false">
            <i class="fa-solid fa-xmark mr-25"></i>Cerrar
          </button>
        </div>
        <label class="inc-label">Descripción</label>
        <b-form-textarea v-model="form.descripcion" rows="2" size="sm" placeholder="Qué ha pasado…" />
        <label class="inc-label mt-50">Notas</label>
        <b-form-textarea v-model="form.notas" rows="2" size="sm" placeholder="Notas internas (opcional)" />
        <div class="d-flex align-items-end mt-50" style="gap: 12px">
          <div>
            <label class="inc-label">Estado</label>
            <b-form-select v-model="form.estado" :options="estados" size="sm" style="width: 140px" />
          </div>
          <div class="flex-grow-1">
            <label class="inc-label">Fotos</label>
            <b-form-file
              v-model="archivos"
              multiple
              accept="image/*"
              size="sm"
              browse-text="Elegir"
              placeholder="Subir fotos…"
            />
          </div>
          <b-button
            variant="primary"
            :disabled="guardando || !form.descripcion.trim()"
            @click="crear"
          >
            <b-spinner v-if="guardando" small class="mr-50" />
            Añadir
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BButton, BFormTextarea, BFormSelect, BFormFile, BFormInput,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: {
    BModal, BSpinner, BButton, BFormTextarea, BFormSelect, BFormFile, BFormInput,
  },
  data() {
    return {
      show: false,
      pedido: null,
      incidencias: [],
      fotoUrls: {},
      cargando: false,
      guardando: false,
      mostrarForm: false,
      nuevoComentario: {},
      editandoId: null,
      editandoTexto: '',
      archivos: [],
      form: { descripcion: '', notas: '', estado: 'abierta' },
      estados: [
        { value: 'abierta', text: 'Abierta' },
        { value: 'en curso', text: 'En curso' },
        { value: 'resuelta', text: 'Resuelta' },
      ],
    }
  },
  methods: {
    abrir(pedido) {
      this.pedido = pedido
      this.incidencias = []
      this.archivos = []
      this.form = { descripcion: '', notas: '', estado: 'abierta' }
      this.mostrarForm = false
      this.show = true
      this.cargar()
    },
    cargar() {
      this.cargando = true
      axios
        .get(`/escaner/pedidos/${this.pedido.id}/incidencias`)
        .then(({ data }) => {
          this.incidencias = data || []
          this.cargarFotos()
        })
        .catch(() => { this.notificar('No se pudieron cargar las incidencias', 'danger') })
        .finally(() => { this.cargando = false })
    },
    // Las fotos requieren el token, así que se cargan por axios a blob URL
    cargarFotos() {
      this.incidencias.forEach(inc => {
        inc.fotos.forEach(fid => {
          if (this.fotoUrls[fid]) return
          axios
            .get(`/escaner/incidencias/fotos/${fid}`, { responseType: 'blob' })
            .then(({ data }) => { this.$set(this.fotoUrls, fid, URL.createObjectURL(data)) })
            .catch(() => {})
        })
      })
    },
    ampliarFoto(fid) {
      if (this.fotoUrls[fid]) window.open(this.fotoUrls[fid], '_blank')
    },
    claseEstado(estado) {
      return estado === 'resuelta' ? 'ok' : (estado === 'en curso' ? 'curso' : 'abierta')
    },
    leerArchivos() {
      return Promise.all((this.archivos || []).map(file => new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve({ mime: file.type || 'image/jpeg', data: reader.result })
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(file)
      }))).then(fotos => fotos.filter(Boolean))
    },
    crear() {
      this.guardando = true
      this.leerArchivos()
        .then(fotos => axios.post(`/escaner/pedidos/${this.pedido.id}/incidencias`, { ...this.form, fotos }))
        .then(() => {
          this.notificar('Incidencia creada')
          this.archivos = []
          this.form = { descripcion: '', notas: '', estado: 'abierta' }
          this.mostrarForm = false
          this.cargar()
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo crear la incidencia'
          this.notificar(msg, 'danger')
        })
        .finally(() => { this.guardando = false })
    },
    comentar(inc) {
      const texto = (this.nuevoComentario[inc.id] || '').trim()
      if (!texto) return
      axios
        .post(`/escaner/incidencias/${inc.id}/comentarios`, { texto })
        .then(() => {
          this.$set(this.nuevoComentario, inc.id, '')
          this.cargar()
        })
        .catch(() => { this.notificar('No se pudo añadir el comentario', 'danger') })
    },
    empezarEdicion(c) {
      this.editandoId = c.id
      this.editandoTexto = c.texto
    },
    guardarEdicion(c) {
      const texto = this.editandoTexto.trim()
      if (!texto) return
      axios
        .put(`/escaner/incidencias/comentarios/${c.id}`, { texto })
        .then(() => {
          this.editandoId = null
          this.cargar()
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo editar el comentario'
          this.notificar(msg, 'danger')
        })
    },
    borrarComentario(c) {
      axios
        .delete(`/escaner/incidencias/comentarios/${c.id}`)
        .then(() => { this.cargar() })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo borrar el comentario'
          this.notificar(msg, 'danger')
        })
    },
    cambiarEstado(inc, estado) {
      axios
        .put(`/escaner/incidencias/${inc.id}`, { estado })
        .then(() => {
          this.$set(inc, 'estado', estado)
          this.notificar(`Incidencia #${inc.id}: ${estado}`)
        })
        .catch(() => { this.notificar('No se pudo cambiar el estado', 'danger') })
    },
    eliminar(inc) {
      this.$bvModal
        .msgBoxConfirm('¿Eliminar la incidencia y sus fotos?', {
          title: 'Confirmar', okVariant: 'danger', okTitle: 'Eliminar', cancelTitle: 'Cancelar', centered: true,
        })
        .then(ok => {
          if (!ok) return
          axios
            .delete(`/escaner/incidencias/${inc.id}`)
            .then(() => { this.notificar('Incidencia eliminada'); this.cargar() })
            .catch(() => { this.notificar('No se pudo eliminar', 'danger') })
        })
    },
    formatFecha(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    },
    notificar(msg, variant = 'success') {
      this.$bvToast.toast(msg, { title: 'Incidencias', variant, solidColor: true })
    },
  },
}
</script>

<style lang="scss" scoped>
$abierta: #ea5455;
$curso: #ff9f43;
$ok: #28c76f;

.inc-listado__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.inc-listado__titulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6e6b7b;
}

.inc-card {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fff;

  &--abierta { background: rgba($abierta, 0.03); }
  &--curso { background: rgba($curso, 0.03); }
  &--ok { background: rgba($ok, 0.03); }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__desc {
    font-weight: 600;
    white-space: pre-line;
  }

  &__notas {
    margin-top: 4px;
    font-size: 12px;
    color: #6e6b7b;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
    padding: 4px 8px;
    display: inline-block;
  }
}

.inc-num {
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  color: #b9b9c3;
}

.inc-estado {
  width: 130px;
  font-weight: 600;

  &--abierta { color: $abierta; border-color: rgba($abierta, 0.4); }
  &--curso { color: darken($curso, 8%); border-color: rgba($curso, 0.5); }
  &--ok { color: $ok; border-color: rgba($ok, 0.4); }
}

.inc-fotos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.inc-coments {
  margin-top: 10px;
  border-top: 1px solid #ebe9f1;
  padding-top: 8px;
}

.inc-coment {
  margin-bottom: 6px;

  &__meta {
    font-size: 10px;
    font-weight: 600;
    color: #b9b9c3;
  }

  &__accion {
    margin-left: 6px;
    cursor: pointer;

    &:hover { color: #7367f0; }
    &--rojo:hover { color: #ea5455; }
  }

  &__texto {
    font-size: 12px;
    white-space: pre-line;
  }

  &__nuevo {
    display: flex;
    gap: 6px;
    margin-top: 6px;
  }
}

.inc-foto {
  width: 84px;
  height: 84px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebe9f1;
  cursor: pointer;
}

.inc-nueva {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
  background: #f8f8fc;

  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    margin-bottom: 10px;
  }

  &__cerrar {
    border: none;
    background: none;
    font-size: 11px;
    font-weight: 600;
    color: #6e6b7b;
    cursor: pointer;

    &:hover { color: #ea5455; }
  }
}

.inc-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6e6b7b;
  margin-bottom: 2px;
  display: block;
}
</style>
